'use client';

import { useQuery } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from 'components/ui/sidebar';
import { useIsPLOnly } from 'hooks/use-auth';
import { useDashboardsList } from 'hooks/use-dashboards';
import every from 'lodash/every';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import flatMap from 'lodash/flatMap';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import map from 'lodash/map';
import maxBy from 'lodash/maxBy';
import replace from 'lodash/replace';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import {
  BarChart2,
  ChevronRight,
  Crosshair,
  Database,
  FileChartColumn,
  FileText,
  Globe,
  LayoutDashboard,
  NotebookText,
  Ship,
} from 'lucide-react';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { SidebarFloatingMenu } from './sidebar-floating-menu';

// Define the items for the sidebar
type SidebarItem = {
  title: string;
  url: string;
  icon?: React.ElementType;
  isActive?: boolean;
  items?: {
    title: string;
    url: string;
  }[];
};

const sidebarItems: SidebarItem[] = [
  {
    title: 'Watchlists',
    url: '/watchlists',
    icon: FileChartColumn,
    items: [],
  },
  {
    title: 'Margins',
    url: '/dashboard/398?pageName=8c986c85983649d42e79',
    icon: BarChart2,
    items: [],
  },
  {
    title: 'Fundamentals Summary',
    url: '/fundamentals-summary',
    icon: FileText,
    items: [
      { title: 'Balances', url: '/dashboard/397' },
      { title: 'Demand', url: '/dashboard/413' },
      { title: 'Output & Yield', url: '/dashboard/410' },
      { title: 'Refinery Runs', url: '/dashboard/408' },
      { title: 'Stocks', url: '/dashboard/409' },
      { title: 'Supply', url: '/dashboard/407' },
    ],
  },
  {
    title: 'Commitment of Traders',
    url: '/cot',
    icon: Database,
    items: [],
  },
  {
    title: 'Open Interest',
    url: '/open-interest',
    icon: BarChart2,
    items: [],
  },
  {
    title: 'Global Flows',
    url: '/global-flows',
    icon: Globe,
    items: [],
  },
  {
    title: 'Geopolitics',
    url: '/geopolitics',
    icon: Crosshair,
    items: [],
  },
  {
    title: 'Dashboards',
    url: '/dashboard',
    icon: LayoutDashboard,
    items: [],
  },
  {
    title: 'Daily Reports',
    url: '/daily-reports',
    icon: NotebookText,
    items: [],
  },
];

// Add a type guard for plDashboards
function isDashboardArray (arr: any): arr is { title: string; url: string }[] {
  return isArray(arr) && every(arr, item => isObject(item) && item !== null && 'title' in item && 'url' in item);
}

// Strip the query string so URLs like '/dashboard/398?pageName=...' compare cleanly against location.pathname.
const pathFromUrl = (url: string) => split(url, '?')[0];

// True when `pathname` is `target` exactly or a child of it (e.g. /watchlists/123).
const matchesPath = (pathname: string, target: string) =>
  pathname === target || startsWith(pathname, target + '/');

// Insert `newItem` immediately after the item whose `title` matches `targetTitle`.
// Falls back to appending if no match. Used so the Petro-Logistics entry slots in
// after Global Flows when present, without three near-duplicate branches.
function insertAfter<T extends { title: string }> (items: T[], targetTitle: string, newItem: T): T[] {
  const idx = findIndex(items, ['title', targetTitle]);
  if (idx === -1) return [...items, newItem];
  return [...items.slice(0, idx + 1), newItem, ...items.slice(idx + 1)];
}

// Renders the apps nav as a fragment of <SidebarMenuItem>s. Consumers own the
// surrounding <SidebarMenu> so collapsed mode can compose apps + agents into
// a single list.
export function NavApps ({
  items: navItems = sidebarItems,
}: {
  items?: SidebarItem[]
}) {

  const { state: sidebarState, isMobile } = useSidebar();
  const { data: dashboardsList } = useDashboardsList(true);
  // HACK: PL-only users see only the Petro-Logistics Shiptracking nav item.
  // Frontend-only gate; backend APIs are still reachable. Replace with proper
  // server-side enforcement.
  const isPLOnly = useIsPLOnly();
  type Dashboard = { name: string; url?: string; id: string | number };
  const plDashboards = sortBy(
    map(
      filter(dashboardsList || [], (dashboard: Dashboard) =>
        includes(toLower(dashboard.name), toLower('Petro-Logistics')) &&
        replace(dashboard.name, 'Petro-Logistics ', '') !== 'Summary Page'
      ),
      (dashboard: Dashboard) => ({
        title: replace(dashboard.name, 'Petro-Logistics ', ''),
        url: dashboard.url || `/dashboard/${dashboard.id}`,
      })
    ),
    'title'
  );

  // Fetch public watchlists
  const { data: publicWatchlists = [] } = useQuery({
    queryKey: ['public-watchlists'],
    queryFn: async () => {
      const client = await OilyticsClient.create();
      const res = await client.getPublicWatchlists();
      return res.data as { id: string; name: string }[];
    },
  });

  // Process public watchlists: sort alphabetically and remove "Oilytics " prefix
  const publicWatchlistItems = React.useMemo(() => {
    return sortBy(
      map(publicWatchlists, (watchlist) => {
        let title = watchlist.name;
        if (startsWith(title, 'Oilytics ')) {
          title = replace(title, 'Oilytics ', '');
        }
        return {
          title,
          url: `/watchlists/${watchlist.id}`,
        };
      }),
      'title'
    );
  }, [publicWatchlists]);

  // Combine items with plDashboards and public watchlists
  const combinedItems = React.useMemo(() => {
    const plSubItems = isDashboardArray(plDashboards) ? plDashboards : [];

    // HACK: PL-only users get a sidebar containing nothing but the
    // Petro-Logistics Shiptracking entry. Everything else (Agents/MCP UI
    // included) is hidden. Strictly a UX gate — backend still serves the
    // data. Note the URL differs from the non-PL entry below.
    if (isPLOnly) {
      return [{
        title: 'Petro-Logistics Shiptracking',
        url: '/petrologistics-shiptracking',
        icon: Ship,
        items: plSubItems,
      }];
    }

    const withWatchlists = map(navItems, (item) =>
      item.title === 'Watchlists' ? { ...item, items: publicWatchlistItems } : item
    );

    if (plDashboards.length === 0) return withWatchlists;

    return insertAfter(withWatchlists, 'Global Flows', {
      title: 'Petro-Logistics Shiptracking',
      url: '/dashboard/416',
      icon: Ship,
      items: plSubItems,
    });
  }, [navItems, plDashboards, publicWatchlistItems, isPLOnly]);

  const { pathname } = useLocation();
  // Resolve which item URL "owns" the current pathname using longest-prefix-wins.
  // Example: on /dashboard/398, both Margins (/dashboard/398) and Dashboards (/dashboard)
  // are matches; the longer/more specific path wins.
  const activePath = React.useMemo(() => {
    const allUrls = flatMap(combinedItems as SidebarItem[], (item) => [
      pathFromUrl(item.url),
      ...map(
        isArray(item.items) ? item.items as { url: string }[] : [],
        (sub) => pathFromUrl(sub.url),
      ),
    ]);
    const matching = filter(allUrls, (target) => matchesPath(pathname, target));
    return maxBy(matching, (p) => p.length);
  }, [combinedItems, pathname]);

  return (
    <>
      {map(combinedItems as SidebarItem[], (item) => {
        const subItems: { title: string; url: string }[] =
            isArray(item.items) &&
            every(item.items, i => isObject(i) && i !== null && 'title' in i && 'url' in i)
              ? (item.items as { title: string; url: string }[])
              : [];
        const hasSubItems = subItems.length > 0;
        const shouldRenderFloatingSubmenu = hasSubItems && sidebarState === 'collapsed' && !isMobile;
        const itemPath = pathFromUrl(item.url);
        // Item is active when it owns the current pathname OR when one of its
        // sub-items does (so the parent stays highlighted on detail pages).
        const isItemActive =
          itemPath === activePath ||
          some(subItems, (sub) => pathFromUrl(sub.url) === activePath);
        const menuButton = (
          <SidebarMenuButton
            asChild
            // size="sm" → h-7 text-xs to match the denser SVG layout
            size="sm"
            isActive={isItemActive}
            tooltip={shouldRenderFloatingSubmenu ? undefined : item.title}
            className="flex-grow"
          >
            <NavLink to={item.url}>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </NavLink>
          </SidebarMenuButton>
        );

        return (
          <Collapsible key={item.title} defaultOpen={Boolean((item as { isActive?: boolean }).isActive)} className="group/collapsible">
            <SidebarMenuItem className="flex flex-col">
              <div className="flex w-full items-center">
                {shouldRenderFloatingSubmenu
                  ? <SidebarFloatingMenu trigger={menuButton} title={item.title} items={subItems} />
                  : menuButton}

                {hasSubItems && (
                  <CollapsibleTrigger className="ml-auto p-1 hover:bg-sidebar-accent rounded-md group-data-[collapsible=icon]:hidden">
                    <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                )}
              </div>

              {hasSubItems && (
                <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden transition-all">
                  <SidebarMenuSub>
                    {map(subItems, (subItem: { title: string; url: string }) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild size="sm" isActive={pathFromUrl(subItem.url) === activePath}>
                          <NavLink to={subItem.url}>
                            <span>{subItem.title}</span>
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              )}
            </SidebarMenuItem>
          </Collapsible>
        );
      })}
    </>
  );
}
